'use client';
import type { Ref } from 'react';
import { useId } from 'react';

import type { TModalProps } from '~components/Modals/types';
import { Text } from '~components/Typography';
import { useModal } from '~hooks/useModal';
import { Textarea } from '~shared/ui/atoms';
import { TooltipButton } from '~shared/ui/molecules/TooltipButton/TooltipButton';

import { LabelRow, Wrapper } from './styles';

export type Props = {
	ref?: Ref<HTMLTextAreaElement | null>;
	label?: React.ReactNode;
	labelChildren?: React.ReactNode;
	tooltip?: React.ReactNode;
	error?: string;
	description?: string;
	renderInfo?: (props: TModalProps) => JSX.Element;
	onTooltipShow?: () => void;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextareaField = ({
	ref,
	label,
	tooltip,
	error,
	description,
	renderInfo,
	onTooltipShow,
	labelChildren,
	...rest
}: Props) => {
	const id = useId();

	const { open, isOpen, close } = useModal();

	return (
		<>
			<Wrapper data-name="textarea-field">
				{label && (
					<LabelRow>
						<label htmlFor={id}>
							<Text size="M">{label}</Text>
						</label>
						{renderInfo ? (
							<TooltipButton
								onClick={() => {
									open()();
									onTooltipShow?.();
								}}
							/>
						) : tooltip ? (
							<TooltipButton placement="top-end" onShow={onTooltipShow}>
								<Text as="div" size="M" align="left">
									{tooltip}
								</Text>
							</TooltipButton>
						) : null}

						{labelChildren}
					</LabelRow>
				)}

				<Textarea ref={ref} id={id} aria-invalid={!!error} {...rest} />

				{error ? (
					<Text size="M" align="left" color="warn">
						{error}
					</Text>
				) : description ? (
					<Text size="M" align="left" color="semi-transparent">
						{description}
					</Text>
				) : null}
			</Wrapper>

			{renderInfo &&
				renderInfo({
					open: isOpen,
					locked: false,
					onClose: close,
				})}
		</>
	);
};
